import React, { ComponentProps, useContext, useEffect, useState } from "react";
import { flatMap } from "lodash";
import { useTranslation } from "react-i18next";
import { PROPERTY_ATTRIBUTE_NAMES } from "common/constants";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { getCustomDisclaimerTexts } from "common-client/utils/disclaimerOverrides";
import {
  FIRMWithWarnings,
  PROPERTY_PANEL_FIRM_WARNINGS,
} from "common-client/utils/firmInfoWarnings";
import { EditableMode, Property } from "common-client/utils/firms";
import { useIsScreenSize } from "../../../../hooks/useWindowSize";
import { AuthContext } from "../../../Authorization/AuthContext";
import { Account } from "../../../Authorization/types";
import { TableCell, TableRow } from "../../../Common/__styles__/StripedTable";
import Disclaimer from "../../../Common/Disclaimer";
import { EmptyState } from "../../../Common/EmptyState";
import { Body } from "../../../Common/Typography";
import { ApproximateBfeTool } from "../../../Maps/ApproximateBfeTool";
import { canUseApproximateBfeTool } from "../../../Maps/ApproximateBfeTool/utils";
import { LayerContext } from "../../../Maps/layers";
import { AddressPanelContext } from "../../AddressPanelContext";
import {
  NO_FIRM_ID,
  PropertyOverviewContext,
} from "../PropertyOverviewContext";
import { BannerWrapper } from "./__styles__/FIRMInfo";
import BFDOverriddenDisclaimer from "./BFDOverriddenDisclaimer";
import BFEOverriddenDisclaimer from "./BFEOverriddenDisclaimer";
import DigitizedFIRMDisclaimer from "./DigitizedFIRMDisclaimer";
import { useFIRMComparisonModal } from "./FIRMComparisonModal";
import { FIRMInfoContext, FIRMInfoContextProvider } from "./FIRMInfoContext";
import { useEditFIRMInfoModal } from "./FIRMInfoModals";
import FIRMSelect from "./FIRMSelect";
import FIRMTable from "./FIRMTable";
import { FIRMWarningsBanner } from "./FIRMWarningsBanner";
import GeometryDisclaimer from "./GeometryDisclaimer";

export type EditFIRMInfoModalState = Maybe<{
  mode: EditableMode;
  firm: FIRMWithWarnings;
  property: NonNullable<Property>;
}>;

const FIRMInfo = ({
  section,
}: {
  section: Account["propertyInformationSections"][number];
}) => {
  const { account, isPublic } = useContext(AuthContext);
  const {
    toggleLayer,
    visibleFIRM: getMapLayerFIRM,
    approximateBfeToolDispatch,
  } = useContext(LayerContext);
  const { property, loadingProperty } = useContext(AddressPanelContext);
  const {
    activeFirmIds,
    firms,
    loading: loadingFirmData,
    setActiveFirmIds,
    getPopulatedFirms,
  } = useContext(FIRMInfoContext);
  const propertyId = property?.id;
  const { t } = useTranslation();

  const [editFirmInfoProps, setEditFirmInfoProps] =
    useState<EditFIRMInfoModalState>(null);

  const activeFIRM = firms.find(firm => firm.id === activeFirmIds[0]);

  const onFIRMEdit = ({
    mode,
    firm = activeFIRM!,
  }: {
    mode: EditableMode;
    firm?: FIRMWithWarnings;
  }) => {
    if (property) {
      setEditFirmInfoProps({ mode, firm, property });
    }
  };

  const isDesktop = useIsScreenSize("desktop");

  const showApproximateBfeToolOption = canUseApproximateBfeTool({
    displayConfig: section.accountPropertyAttributes.find(
      attribute => attribute.name === PROPERTY_ATTRIBUTE_NAMES.BFE,
    )?.displayConfig,
    firm: activeFIRM ?? null,
    isDesktop,
  });

  const openApproximateBfeTool = () => {
    setEditFirmInfoProps(null);

    if (!mapLayerFIRM || !property?.id || !activeFIRM) return;

    approximateBfeToolDispatch?.({
      type: "setMode",
      data: {
        mode: "on",
        render: () => {
          return (
            <ApproximateBfeTool
              openEditFIRM={() => onFIRMEdit({ mode: "elevation" })}
              propertyId={property.id}
              mapLayerFirm={mapLayerFIRM}
            />
          );
        },
      },
    });
  };

  const [showFIRMInfoModal, hideFIRMInfoModal] = useEditFIRMInfoModal({
    mode: editFirmInfoProps?.mode,
    firm: editFirmInfoProps?.firm,
    property: editFirmInfoProps?.property,
    clearEditFirmInfoProps: () => {
      setEditFirmInfoProps(null);
    },
    ...(showApproximateBfeToolOption && {
      openApproximateBfeTool: () => {
        setEditFirmInfoProps(null);
        openApproximateBfeTool();
      },
    }),
  });

  useEffect(() => {
    if (editFirmInfoProps?.mode) {
      showFIRMInfoModal();
    } else {
      hideFIRMInfoModal();
    }
  }, [editFirmInfoProps?.mode, showFIRMInfoModal, hideFIRMInfoModal]);

  const mapLayerFIRM = getMapLayerFIRM() ?? null;
  const mapLayerFIRMId = mapLayerFIRM?.id;

  // FIRM layer changes in layer control update PIP FIRM
  useEffect(() => {
    if (mapLayerFIRMId) {
      setActiveFirmIds([mapLayerFIRMId]);
    }
  }, [mapLayerFIRMId]);

  // If the map has FIRM layer on, then changing activeFIRMId updates FIRM layer on map
  useEffect(() => {
    if (
      mapLayerFIRM &&
      activeFirmIds[0] &&
      activeFirmIds[0] !== mapLayerFIRMId
    ) {
      toggleLayer({ group: "firms", id: activeFirmIds[0], isVisible: true });
    }
  }, [activeFirmIds]);

  const [showFIRMComparisonModal, hideFIRMComparisonModal] =
    useFIRMComparisonModal({
      property,
      account: account!,
      onFIRMEdit: ({
        mode,
        firm,
      }: {
        mode: EditableMode;
        firm: FIRMWithWarnings;
      }) => {
        hideFIRMComparisonModal?.();
        onFIRMEdit({ mode });
        setActiveFirmIds([firm.id]);
      },
      onFIRMClick: (firmId: string) => {
        hideFIRMComparisonModal?.();
        setActiveFirmIds([firmId]);
      },
      section,
    });

  if (loadingProperty || loadingFirmData || !activeFIRM)
    return <em>{t("common-loading")}</em>;

  if (arrayHasAtLeastOneItem(firms) && firms[0].id === NO_FIRM_ID) {
    return (
      <div>
        <Body size="large" type="emphasis" style={{ maxWidth: "50%" }}>
          {section.label}
        </Body>
        <EmptyState compact title={t("property-flood-info-empty-state")} />
      </div>
    );
  }

  const showFIRMSelect = getPopulatedFirms().length > 1;

  const TableHeader = () => {
    return (
      <TableRow header>
        <TableCell wrap={true}>{section.label}</TableCell>
        <TableCell>
          <FIRMSelect onComparisonSelection={showFIRMComparisonModal!} />
        </TableCell>
      </TableRow>
    );
  };

  const customDisclaimerTexts = getCustomDisclaimerTexts({
    disclaimerOverrides: account?.disclaimerOverrides,
    isPublic,
  });

  return (
    <div data-testid="firm-info">
      <FIRMTable
        account={account!}
        property={property}
        onFIRMEdit={onFIRMEdit}
        Header={showFIRMSelect ? TableHeader : undefined}
        showTitle={true}
        section={section}
      />

      <BannerWrapper>
        <FIRMWarningsBanner
          firms={[activeFIRM]}
          propertyId={propertyId!}
          warningTextConfig={PROPERTY_PANEL_FIRM_WARNINGS}
          title={section.label}
        />
      </BannerWrapper>

      {customDisclaimerTexts.map((disclaimerText, i) => (
        <Disclaimer key={i} message={disclaimerText} />
      ))}
      <DigitizedFIRMDisclaimer accountFIRMData={firms} />
      <BFEOverriddenDisclaimer
        firm={activeFIRM}
        property={property}
        accountPropertyAttributes={flatMap(
          account?.propertyInformationSections.map(
            section => section.accountPropertyAttributes,
          ),
        )}
      />
      <BFDOverriddenDisclaimer firm={activeFIRM} />
      <GeometryDisclaimer />
    </div>
  );
};

const FIRMInfoWrapper = (props: ComponentProps<typeof FIRMInfo>) => {
  const { longitude, latitude } = useContext(PropertyOverviewContext);
  return (
    <FIRMInfoContextProvider longitude={longitude!} latitude={latitude!}>
      <FIRMInfo {...props} />
    </FIRMInfoContextProvider>
  );
};

export default FIRMInfoWrapper;
